import { useEffect, useState, useCallback } from "react";
import { T, base } from "../lib/theme";
import { apiFetch } from "../lib/api";
import { Btn } from "../components";
import Icon from "../components/Icon";
import useIsMobile from "../hooks/useIsMobile";

// Hide native calendar indicator, keep it clickable, custom icon shows through
if (typeof document !== "undefined" && !document.getElementById("__todo-dp-style")) {
  const s = document.createElement("style");
  s.id = "__todo-dp-style";
  s.textContent = `
    input[type="datetime-local"]::-webkit-calendar-picker-indicator {
      opacity: 0; position: absolute; right: 0;
      width: 36px; height: 100%; cursor: pointer;
    }
  `;
  document.head.appendChild(s);
}

// ── Helpers ───────────────────────────────────────────────────────
function pad(n) { return String(n).padStart(2, "0"); }

function toLocal(d) {
  if (!d) return "";
  const dt = new Date(d);
  if (isNaN(dt)) return "";
  return `${dt.getFullYear()}-${pad(dt.getMonth()+1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
}

function nowMin() {
  const d = new Date();
  d.setSeconds(0, 0);
  return toLocal(d);
}

function fmtDisplay(v) {
  if (!v) return "";
  const d = new Date(v);
  return d.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function isAllSent(todo) {
  if (todo.reminders?.length) return todo.reminders.every(r => r.sent);
  return todo.reminderSent;
}

// ── Field label ───────────────────────────────────────────────────
const FL = ({ children }) => (
  <label style={{ fontSize: 10, fontWeight: 700, color: T.text3, textTransform: "uppercase",
    letterSpacing: "0.07em", display: "block", marginBottom: 7 }}>
    {children}
  </label>
);

// ── DateTime picker with clock icon overlay ───────────────────────
function DTPicker({ value, onChange, min, max, label }) {
  const [focus, setFocus] = useState(false);
  return (
    <div>
      {label && <FL>{label}</FL>}
      <div style={{ position: "relative" }}>
        <input
          type="datetime-local"
          value={value}
          onChange={onChange}
          min={min}
          max={max}
          onFocus={() => setFocus(true)}
          onBlur={() => setFocus(false)}
          style={{
            width: "100%", padding: "10px 38px 10px 12px", boxSizing: "border-box",
            background: T.bg1, border: `1.5px solid ${focus ? T.indigo : T.border2}`,
            borderRadius: 10, color: value ? T.text1 : T.text3,
            fontSize: 13, outline: "none", fontFamily: base.font,
            transition: "border-color 0.15s",
          }}
        />
        <div style={{ position: "absolute", right: 11, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
            stroke={focus ? T.indigo : T.text3} strokeWidth="2" strokeLinecap="round">
            <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
          </svg>
        </div>
      </div>
    </div>
  );
}

// ── Reminders editor ──────────────────────────────────────────────
const BEFORE_PRESETS = [
  { label: "At task time", mins: 0  },
  { label: "10 min before", mins: 10  },
  { label: "20 min before", mins: 20  },
  { label: "30 min before", mins: 30  },
  { label: "1 hr before",   mins: 60  },
  { label: "2 hr before",   mins: 120 },
  { label: "3 hr before",   mins: 180 },
];

function presetISO(preset, dueLocal) {
  if (!dueLocal) return null;
  const d = new Date(dueLocal);
  d.setMinutes(d.getMinutes() - preset.mins);
  if (d <= new Date()) return null; // already past
  return toLocal(d);
}

function RemindersEditor({ reminders, onChange, dueDate }) {
  const [showCustom, setShowCustom] = useState(false);
  const [customVal, setCustomVal]   = useState("");
  const now = nowMin();

  const add = (iso) => {
    if (!iso || reminders.includes(iso)) return;
    onChange([...reminders, iso]);
  };

  const addCustom = () => {
    if (!customVal) return;
    add(customVal);
    setCustomVal("");
    setShowCustom(false);
  };

  const remove = (i) => onChange(reminders.filter((_, idx) => idx !== i));

  return (
    <div>
      <FL>Reminders</FL>

      {/* Preset chips */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
        {dueDate && BEFORE_PRESETS.map(p => {
          const iso = presetISO(p, dueDate);
          const active = iso && reminders.includes(iso);
          const disabled = !iso;
          return (
            <button key={p.label} type="button"
              onClick={() => !disabled && add(iso)}
              disabled={disabled}
              style={{
                padding: "5px 11px", borderRadius: 8, fontSize: 11, fontWeight: 700,
                cursor: disabled ? "not-allowed" : "pointer", fontFamily: base.font,
                border: `1.5px solid ${active ? T.indigo : disabled ? T.border1 : T.border2}`,
                background: active ? T.indigoDim : "transparent",
                color: active ? T.indigo : disabled ? T.text3 : T.text2,
                opacity: disabled ? 0.4 : 1, transition: "all 0.12s",
              }}>
              {p.label}
            </button>
          );
        })}

        {!dueDate && (
          <div style={{ fontSize: 11, color: T.text3, padding: "5px 0", lineHeight: 1.4 }}>
            Set task date &amp; time above to use quick presets
          </div>
        )}
      </div>

      {/* Custom toggle */}
      <div style={{ marginBottom: showCustom ? 10 : 0 }}>
        <button type="button" onClick={() => {
            // Opening: prefill with the task time so the picker opens near the task
            // date (user can then move forward/back). Keeps whatever was typed.
            if (!showCustom && dueDate && !customVal) setCustomVal(dueDate);
            setShowCustom(v => !v);
          }}
          style={{
            padding: "5px 13px", borderRadius: 8, fontSize: 11, fontWeight: 700,
            cursor: "pointer", fontFamily: base.font,
            border: `1.5px solid ${showCustom ? T.indigo : T.border2}`,
            background: showCustom ? T.indigoDim : "transparent",
            color: showCustom ? T.indigo : T.text2, transition: "all 0.12s",
          }}>
          + Custom time
        </button>
      </div>

      {/* Custom picker */}
      {showCustom && (
        <div style={{
          padding: "12px", borderRadius: 10, marginBottom: 10,
          background: T.bg1, border: `1px solid ${T.border2}`,
        }}>
          <DTPicker
            value={customVal}
            onChange={e => setCustomVal(e.target.value)}
            min={now}
          />
          <div style={{ fontSize: 11, color: T.text3, marginTop: 6, marginBottom: 8 }}>
            Pick any future time — you can set it before <em>or after</em> the task time.
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button type="button" onClick={() => { setShowCustom(false); setCustomVal(""); }}
              style={{ flex: 1, padding: "8px", borderRadius: 9, fontSize: 12, fontWeight: 600,
                cursor: "pointer", fontFamily: base.font, background: "transparent",
                border: `1px solid ${T.border1}`, color: T.text3 }}>
              Cancel
            </button>
            <button type="button" onClick={addCustom} disabled={!customVal}
              style={{ flex: 2, padding: "8px", borderRadius: 9, fontSize: 12, fontWeight: 700,
                cursor: customVal ? "pointer" : "default", fontFamily: base.font,
                background: T.indigoDim, border: `1.5px solid rgba(99,102,241,0.3)`,
                color: T.indigo, opacity: customVal ? 1 : 0.5 }}>
              Add Reminder
            </button>
          </div>
        </div>
      )}

      {/* Added reminders */}
      {reminders.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
          {reminders.map((r, i) => r && (
            <div key={i} style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "8px 12px", borderRadius: 9,
              background: T.amberDim, border: `1px solid ${T.amber}33`,
            }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: T.amber, display: "flex", alignItems: "center", gap: 6 }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={T.amber} strokeWidth="2"
                  strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                  <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                </svg>
                {fmtDisplay(r)}
              </span>
              <button type="button" onClick={() => remove(i)}
                style={{ width: 20, height: 20, borderRadius: 5, border: "none",
                  background: "transparent", cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
                  stroke={T.amber} strokeWidth="2.5" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      {reminders.length === 0 && !showCustom && dueDate && (
        <p style={{ fontSize: 11, color: T.text3, margin: "6px 0 0" }}>
          Select a preset above or pick a custom time
        </p>
      )}
    </div>
  );
}

// ── Shared form fields (used in create + edit) ────────────────────
function TaskFormFields({ title, setTitle, note, setNote, dueDate, setDueDate, reminders, setReminders, whatsappNumber, setWhatsappNumber, autoFocus, titleError }) {
  const titleInvalid = titleError && !title.trim();
  const handleDueDateChange = (e) => {
    const val = e.target.value;
    setDueDate(val);
    // Reminders are independent of the due date (they may fire before OR after
    // the task), so only drop ones that are now in the past.
    if (val) {
      setReminders(prev => prev.filter(r => {
        const rd = new Date(r);
        return !isNaN(rd) && rd >= new Date();
      }));
    }
  };

  return (
    <>
      {/* Title */}
      <FL>Task Title <span style={{ color: T.rose }}>*</span></FL>
      <input
        autoFocus={autoFocus}
        value={title}
        onChange={e => setTitle(e.target.value)}
        placeholder="e.g. Call the supplier"
        style={{
          width: "100%", padding: "9px 12px", background: T.bg1,
          border: `1px solid ${titleInvalid ? T.rose : T.border2}`, borderRadius: 9,
          color: T.text1, fontSize: 14, fontWeight: 600, outline: "none",
          fontFamily: base.font, boxSizing: "border-box",
          marginBottom: titleInvalid ? 6 : 16, transition: "border-color 0.15s",
        }}
        onFocus={e => e.target.style.borderColor = titleInvalid ? T.rose : T.indigo}
        onBlur={e  => e.target.style.borderColor = titleInvalid ? T.rose : T.border2}
      />
      {titleInvalid && (
        <p style={{ margin: "0 0 14px", fontSize: 11, fontWeight: 600, color: T.rose, display: "flex", alignItems: "center", gap: 5 }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.rose} strokeWidth="2.2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          Task title is required
        </p>
      )}

      {/* Note */}
      <textarea
        value={note}
        onChange={e => setNote(e.target.value)}
        placeholder="Add a note... (optional)"
        rows={2}
        style={{
          width: "100%", padding: "9px 12px", background: T.bg1,
          border: `1px solid ${T.border2}`, borderRadius: 9,
          color: T.text2, fontSize: 13, outline: "none",
          fontFamily: base.font, boxSizing: "border-box",
          resize: "none", marginBottom: 16, transition: "border-color 0.15s",
        }}
        onFocus={e => e.target.style.borderColor = T.indigo}
        onBlur={e  => e.target.style.borderColor = T.border2}
      />

      {/* Task Date & Time */}
      <div style={{ marginBottom: 16 }}>
        <DTPicker
          label="Task Date & Time"
          value={dueDate}
          onChange={handleDueDateChange}
          min={nowMin()}
        />
      </div>

      {/* Reminders */}
      <div style={{
        marginBottom: 16, padding: "14px", borderRadius: 10,
        background: T.bg1, border: `1px solid ${T.border1}`,
      }}>
        <RemindersEditor reminders={reminders} onChange={setReminders} dueDate={dueDate} />
      </div>

      {/* WhatsApp number */}
      <div style={{ marginBottom: 20 }}>
        <FL>WhatsApp Reminder Number <span style={{ fontWeight: 400, textTransform: "none", fontSize: 10, color: T.text3 }}>(optional)</span></FL>
        <div style={{ position: "relative" }}>
          <div style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#25D366" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.22C1.6 2.1 2.43 1.18 3.55 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.91a16 16 0 0 0 6 6l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 18z"/>
            </svg>
          </div>
          <input
            type="tel"
            value={whatsappNumber}
            onChange={e => setWhatsappNumber(e.target.value)}
            placeholder="+923001234567"
            style={{
              width: "100%", padding: "10px 12px 10px 34px", boxSizing: "border-box",
              background: T.bg1, border: `1px solid ${T.border2}`,
              borderRadius: 10, color: T.text1, fontSize: 13, outline: "none",
              fontFamily: base.font, transition: "border-color 0.15s",
            }}
            onFocus={e => e.target.style.borderColor = "#25D366"}
            onBlur={e  => e.target.style.borderColor = T.border2}
          />
        </div>
        <p style={{ margin: "6px 0 0", fontSize: 11, color: T.text3 }}>
          A WhatsApp message will be sent to this number when the reminder fires
        </p>
      </div>

    </>
  );
}

// ── Create form ───────────────────────────────────────────────────
function CreateForm({ token, onCreated }) {
  const [open,       setOpen]       = useState(false);
  const [title,      setTitle]      = useState("");
  const [note,       setNote]       = useState("");
  const [dueDate,    setDueDate]    = useState("");
  const [reminders,       setReminders]       = useState([]);
  const [whatsappNumber,  setWhatsappNumber]  = useState("");
  const [saving,          setSaving]          = useState(false);
  const [titleError,      setTitleError]      = useState(false);

  const reset = () => {
    setTitle(""); setNote(""); setDueDate(""); setReminders([]); setWhatsappNumber(""); setTitleError(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { setTitleError(true); return; }
    setTitleError(false);
    setSaving(true);
    try {
      const body = {
        title: title.trim(), note, whatsappNumber,
        reminders: reminders.filter(r => r).map(r => new Date(r).toISOString()),
      };
      if (dueDate) body.dueDate = new Date(dueDate).toISOString();
      const d = await apiFetch("/api/todos", { method: "POST", body: JSON.stringify(body) }, token);
      if (d.todo) { onCreated(d.todo); reset(); }
    } catch {}
    setSaving(false);
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)}
        style={{
          display: "flex", alignItems: "center", gap: 12,
          width: "100%", padding: "15px 20px",
          background: T.bg2, border: `1.5px dashed ${T.border2}`,
          borderRadius: 14, cursor: "pointer", color: T.text3,
          fontSize: 14, fontFamily: base.font, transition: "all 0.15s",
        }}
        onMouseOver={e => { e.currentTarget.style.borderColor = T.indigo; e.currentTarget.style.color = T.indigo; }}
        onMouseOut={e  => { e.currentTarget.style.borderColor = T.border2; e.currentTarget.style.color = T.text3; }}
      >
        <div style={{ width: 30, height: 30, borderRadius: 9, flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "center", background: T.indigoDim }}>
          <Icon name="plus" size={15} color={T.indigo} />
        </div>
        Add new task...
      </button>
    );
  }

  return (
    <form onSubmit={submit} style={{
      background: T.bg2, border: `1.5px solid ${T.indigo}44`,
      borderRadius: 14, padding: "20px",
    }}>
      <TaskFormFields
        title={title} setTitle={setTitle}
        note={note} setNote={setNote}
        dueDate={dueDate} setDueDate={setDueDate}
        reminders={reminders} setReminders={setReminders}
        whatsappNumber={whatsappNumber} setWhatsappNumber={setWhatsappNumber}
        autoFocus titleError={titleError}
      />
      <div style={{ display: "flex", gap: 10 }}>
        <Btn variant="ghost" type="button" onClick={() => { reset(); setOpen(false); }}>
          Cancel
        </Btn>
        <Btn variant="primary" type="submit" disabled={saving} style={{ flex: 1 }}>
          {saving ? "Saving..." : "Add Task"}
        </Btn>
      </div>
    </form>
  );
}

// ── Todo row ──────────────────────────────────────────────────────
function TodoRow({ todo, token, onUpdate, onDelete }) {
  const [editing,    setEditing]    = useState(false);
  const [title,      setTitle]      = useState(todo.title);
  const [note,       setNote]       = useState(todo.note || "");
  const [dueDate,    setDueDate]    = useState(todo.dueDate ? toLocal(todo.dueDate) : "");
  const [reminders,      setReminders]      = useState(
    todo.reminders?.length
      ? todo.reminders.map(r => toLocal(r.at))
      : todo.reminderAt ? [toLocal(todo.reminderAt)] : []
  );
  const [whatsappNumber, setWhatsappNumber] = useState(todo.whatsappNumber || "");
  const [saving,         setSaving]         = useState(false);
  const [titleError,     setTitleError]     = useState(false);

  const toggle = async () => {
    const d = await apiFetch(`/api/todos/${todo._id}`, {
      method: "PATCH", body: JSON.stringify({ completed: !todo.completed }),
    }, token);
    if (d.todo) onUpdate(d.todo);
  };

  const save = async () => {
    if (!title.trim()) { setTitleError(true); return; }
    setTitleError(false);
    setSaving(true);
    try {
      const body = {
        title: title.trim(), note, whatsappNumber,
        reminders: reminders.filter(r => r).map(r => new Date(r).toISOString()),
      };
      if (dueDate) body.dueDate = new Date(dueDate).toISOString();
      const d = await apiFetch(`/api/todos/${todo._id}`, {
        method: "PATCH", body: JSON.stringify(body),
      }, token);
      if (d.todo) { onUpdate(d.todo); setEditing(false); }
    } catch {}
    setSaving(false);
  };

  const del = async () => {
    await apiFetch(`/api/todos/${todo._id}`, { method: "DELETE" }, token);
    onDelete(todo._id);
  };

  const pendingReminders = todo.reminders?.filter(r => !r.sent) || [];
  const hasPendingLegacy = todo.reminderAt && !todo.reminderSent;

  if (editing) {
    return (
      <div style={{
        background: T.bg2, border: `1.5px solid ${T.indigo}55`,
        borderRadius: 14, padding: "18px 20px", marginBottom: 10,
      }}>
        <TaskFormFields
          title={title} setTitle={setTitle}
          note={note} setNote={setNote}
          dueDate={dueDate} setDueDate={setDueDate}
          reminders={reminders} setReminders={setReminders}
          whatsappNumber={whatsappNumber} setWhatsappNumber={setWhatsappNumber}
          autoFocus titleError={titleError}
        />
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <Btn variant="ghost" onClick={() => setEditing(false)}>Cancel</Btn>
          <Btn variant="primary" onClick={save} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Btn>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      display: "flex", alignItems: "flex-start", gap: 14,
      padding: "15px 18px", borderRadius: 13, marginBottom: 8,
      background: T.bg2, border: `1px solid ${todo.completed ? T.border1 : T.border2}`,
      borderLeft: `3px solid ${todo.completed ? T.border1 : T.indigo}`,
      opacity: todo.completed ? 0.55 : 1, transition: "all 0.15s",
    }}>
      {/* Checkbox */}
      <button onClick={toggle} style={{
        width: 22, height: 22, borderRadius: 7, flexShrink: 0, marginTop: 2,
        border: `2px solid ${todo.completed ? T.indigo : T.border2}`,
        background: todo.completed ? T.indigoDim : "transparent",
        cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
        transition: "all 0.15s",
      }}>
        {todo.completed && <Icon name="check" size={12} color={T.indigo} />}
      </button>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Title row */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
          <span style={{
            fontSize: 14, fontWeight: 600, color: T.text1,
            textDecoration: todo.completed ? "line-through" : "none",
            wordBreak: "break-word",
          }}>
            {todo.title}
          </span>
        </div>

        {/* Note */}
        {todo.note && (
          <div style={{ fontSize: 12, color: T.text3, marginBottom: 8, wordBreak: "break-word", lineHeight: 1.5 }}>
            {todo.note}
          </div>
        )}

        {/* Due date */}
        {todo.dueDate && (
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 5, marginBottom: 6,
            fontSize: 11, fontWeight: 600, color: T.indigo,
            padding: "2px 8px", borderRadius: 6, background: T.indigoDim,
          }}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={T.indigo} strokeWidth="2">
              <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
            </svg>
            {fmtDisplay(todo.dueDate)}
          </div>
        )}

        {/* Pending reminders */}
        {(pendingReminders.length > 0 || hasPendingLegacy) && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
            {pendingReminders.map((r, i) => (
              <span key={i} style={{
                fontSize: 11, display: "flex", alignItems: "center", gap: 4,
                padding: "2px 8px", borderRadius: 6,
                background: T.amberDim, color: T.amber, border: `1px solid ${T.amber}33`,
              }}>
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke={T.amber} strokeWidth="2"
                  strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                  <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                </svg>
                {fmtDisplay(r.at)}
              </span>
            ))}
            {hasPendingLegacy && (
              <span style={{
                fontSize: 11, display: "flex", alignItems: "center", gap: 4,
                padding: "2px 8px", borderRadius: 6,
                background: T.amberDim, color: T.amber, border: `1px solid ${T.amber}33`,
              }}>
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke={T.amber} strokeWidth="2"
                  strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                  <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                </svg>
                {fmtDisplay(todo.reminderAt)}
              </span>
            )}
          </div>
        )}

        {isAllSent(todo) && (todo.reminders?.length > 0 || todo.reminderAt) && (
          <div style={{ fontSize: 11, color: T.text3, marginTop: 4, display: "flex", alignItems: "center", gap: 4 }}>
            <Icon name="check" size={10} color={T.text3} /> All reminders sent
          </div>
        )}
      </div>

      {/* Actions */}
      <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
        <button onClick={() => setEditing(true)} style={{
          width: 32, height: 32, borderRadius: 9, border: `1px solid ${T.border1}`,
          background: "transparent", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s",
        }}
          onMouseOver={e => e.currentTarget.style.background = T.bg1}
          onMouseOut={e  => e.currentTarget.style.background = "transparent"}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="1.8">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
          </svg>
        </button>
        <button onClick={del} style={{
          width: 32, height: 32, borderRadius: 9, border: `1px solid ${T.border1}`,
          background: "transparent", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s",
        }}
          onMouseOver={e => { e.currentTarget.style.background = "rgba(244,63,94,0.08)"; e.currentTarget.style.borderColor = "rgba(244,63,94,0.3)"; }}
          onMouseOut={e  => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = T.border1; }}
        >
          <Icon name="trash" size={13} color="#f43f5e" />
        </button>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────
export function TodoPage({ token, showToast }) {
  const [todos,   setTodos]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter,  setFilter]  = useState("active");
  const isTablet = useIsMobile(980);
  const isPhone = useIsMobile(560);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await apiFetch("/api/todos", {}, token);
      if (d.todos) setTodos(d.todos);
    } catch { showToast?.("Failed to load tasks", "error"); }
    setLoading(false);
  }, [token, showToast]);

  useEffect(() => { load(); }, [load]);

  const onCreated = (t) => setTodos(prev => [t, ...prev]);
  const onUpdate  = (u) => setTodos(prev => prev.map(t => t._id === u._id ? u : t));
  const onDelete  = (id) => setTodos(prev => prev.filter(t => t._id !== id));

  const active    = todos.filter(t => !t.completed);
  const completed = todos.filter(t => t.completed);
  const displayed = filter === "active" ? active : filter === "completed" ? completed : todos;

  const FBtn = ({ id, label, count }) => (
    <button onClick={() => setFilter(id)} style={{
      padding: isPhone ? "8px 10px" : "7px 16px", borderRadius: 10, fontSize: 13, cursor: "pointer",
      fontFamily: base.font, fontWeight: filter === id ? 700 : 500,
      border: `1px solid ${filter === id ? T.indigo : T.border1}`,
      background: filter === id ? T.indigoDim : "transparent",
      color: filter === id ? T.indigo : T.text3, transition: "all 0.12s",
      display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
      flex: isPhone ? "1 1 0" : "0 0 auto",
      minWidth: 0,
    }}>
      {label}
      {count > 0 && (
        <span style={{
          fontSize: 11, padding: "1px 7px", borderRadius: 6, fontWeight: 700,
          background: filter === id ? T.indigo : T.border2,
          color: filter === id ? "#fff" : T.text3,
        }}>
          {count}
        </span>
      )}
    </button>
  );

  return (
    <div style={{
      padding: isPhone ? "18px 14px 28px" : isTablet ? "24px 20px" : "28px 32px",
      animation: "fadeIn 0.25s ease",
      maxWidth: "100%",
      overflowX: "hidden",
    }}>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: T.text1 }}>Tasks</h2>
        <p style={{ margin: "4px 0 0", fontSize: 13, color: T.text3 }}>
          {active.length} active · {completed.length} done
        </p>
      </div>

      <div style={{
        display: "grid",
        gridTemplateColumns: isTablet ? "minmax(0, 1fr)" : "minmax(0, 1fr) 340px",
        gap: isPhone ? 18 : 28,
        alignItems: "start",
      }}>

        {/* ── Left: task list ── */}
        <div style={{ minWidth: 0, order: isTablet ? 1 : 0 }}>
          {/* Filter bar */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 18, flexWrap: "wrap" }}>
            <FBtn id="active"    label="Active"    count={active.length}    />
            <FBtn id="completed" label="Completed" count={completed.length} />
            <FBtn id="all"       label="All"       count={todos.length}     />
          </div>

          {loading ? (
            <div style={{ textAlign: "center", color: T.text3, padding: "60px 0", fontSize: 13 }}>
              Loading tasks...
            </div>
          ) : displayed.length === 0 ? (
            <div style={{
              textAlign: "center", padding: "60px 0",
              background: T.bg2, border: `1px solid ${T.border1}`, borderRadius: 14,
            }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={T.text3}
                strokeWidth="1.2" style={{ opacity: 0.4 }}>
                <path d="M9 11l3 3L22 4"/>
                <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>
              </svg>
              <div style={{ fontSize: 15, fontWeight: 600, color: T.text2, marginTop: 12 }}>
                {filter === "completed" ? "No completed tasks yet" : "No tasks yet"}
              </div>
              {filter === "active" && (
                <div style={{ fontSize: 13, color: T.text3, marginTop: 6 }}>
                  Create a task using the form on the right
                </div>
              )}
            </div>
          ) : (
            displayed.map(todo => (
              <TodoRow key={todo._id} todo={todo} token={token} onUpdate={onUpdate} onDelete={onDelete} />
            ))
          )}
        </div>

        {/* ── Right: create form (sticky) ── */}
        <div style={{ position: isTablet ? "static" : "sticky", top: 20, minWidth: 0, order: isTablet ? 0 : 1 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: T.text3, textTransform: "uppercase",
            letterSpacing: "0.07em", marginBottom: 12 }}>
            New Task
          </div>
          <CreateForm token={token} onCreated={onCreated} />
        </div>

      </div>
    </div>
  );
}

export default TodoPage;
